/**
 * Play — the chat surface page. Composes `ChatPanel` + `ChatComposer` and
 * wires them to `useChat`.
 *
 * Post-ADR-001 (Tailwind v4 + Radix UI): no Cloudscape imports. The page
 * mirrors the Forest editorial language used by `Home.tsx` — Tiempos serif
 * headings, hairline borders, generous whitespace.
 *
 * Layout: 3-panel — [Conversation Panel (240px) | Chat Area (flex-1) | Right Panel (AppShell)]
 * The right panel is rendered by AppShell; this page renders the left
 * conversation panel and the center chat area.
 *
 * Session lifecycle: the page resolves a real backend session via
 * `useActiveSession()` which either returns the most recent active session
 * or creates one on mount. The session ID is then passed to `useChat` for
 * the streaming WS bridge.
 *
 * WebSocket integration: subscribes to the WsBridge for real-time streaming.
 * If the WS is unavailable the page degrades to REST-only (POST /chat/message
 * returns the complete response). A small status indicator shows connection
 * health in the header area.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Share2 } from 'lucide-react';
import AccessibleAnnouncer from '@/components/common/AccessibleAnnouncer';
import ChatPanel from '@/components/organisms/ChatPanel';
import ChatComposer from '@/components/organisms/ChatComposer';
import ShareModal from '@/components/molecules/ShareModal';
import ConversationContextMenu from '@/components/molecules/ConversationContextMenu';
import { useChat } from '@/hooks/useChat';
import { useActiveSession } from '@/hooks/useActiveSession';
import { useSessionFiles } from '@/hooks/useSessionFiles';
import { useWsSession } from '@/services/ws/useWsSession';
import type { WsStatus } from '@/services/ws/socket';
import { AGENTS, type AgentId } from '@/tokens/colors';
import { usePageEnter, useStaggerAnimation } from '@/hooks/useAppleMotion';
import { wayneFetch } from '@/api/wayneFetch';
import { submitChatFullAuto } from '@/api/tasks';
import { listSessions as listSessionsGenerated, searchSessions as searchSessionsGenerated } from '@/api/generated/sdk.gen';
import type { SessionSummary } from '@/api/generated/types.gen';
import { createSession } from '@/api/services/sessions.service';

// -- Types for conversation panel ---------------------------------------------

interface ConversationItem {
  readonly id: string;
  readonly title: string;
  readonly agentId: AgentId;
  readonly time: string;
  readonly spaceId?: string;
}

interface SpaceItem {
  readonly id: string;
  readonly name: string;
  readonly count: number;
}

// -- API response types for spaces (not yet in generated SDK) -----------------

interface SpaceApiItem {
  readonly spaceId: string;
  readonly name: string;
}

interface SpacesResponse {
  readonly spaces: readonly SpaceApiItem[];
}

// -- Helpers ------------------------------------------------------------------

/** Format an ISO date string into a short relative time label. */
function formatRelativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  if (diff < 0) return 'now';
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w`;
}

/** Custom hook: debounce a string value by the given delay (ms). */
function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (value === '') {
      setDebounced('');
      return;
    }
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

// -- Conversation Panel -------------------------------------------------------

function ConversationPanel({
  conversations,
  spaces,
  activeConversationId,
  onSelectConversation,
  onNewChat,
  activeSpaceId,
  onSelectSpace,
  searchQuery,
  onSearchChange,
  isSearching,
  sessionsLoading,
  isCreating,
}: {
  readonly conversations: readonly ConversationItem[];
  readonly spaces: readonly SpaceItem[];
  readonly activeConversationId: string;
  readonly onSelectConversation: (id: string) => void;
  readonly onNewChat: () => void;
  readonly activeSpaceId: string | null;
  readonly onSelectSpace: (spaceId: string | null) => void;
  readonly searchQuery: string;
  readonly onSearchChange: (query: string) => void;
  readonly isSearching: boolean;
  readonly sessionsLoading: boolean;
  readonly isCreating: boolean;
}): ReactElement {
  const queryClient = useQueryClient();
  const [expandedSpaces, setExpandedSpaces] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  // Auto-expand the first space when spaces load
  useEffect(() => {
    if (spaces.length > 0 && expandedSpaces.size === 0) {
      setExpandedSpaces(new Set([spaces[0]!.id]));
    }
  }, [spaces, expandedSpaces.size]);

  const toggleSpace = useCallback((spaceId: string) => {
    setExpandedSpaces((prev) => {
      const next = new Set(prev);
      if (next.has(spaceId)) {
        next.delete(spaceId);
      } else {
        next.add(spaceId);
      }
      return next;
    });
  }, []);

  const handleDropOnSpace = useCallback(
    (spaceId: string, sessionId: string) => {
      void wayneFetch(`/sessions/${sessionId}`, {
        method: 'PUT',
        body: JSON.stringify({ spaceId }),
      }).then(() => {
        void queryClient.invalidateQueries({ queryKey: ['sessions'] });
        void queryClient.invalidateQueries({ queryKey: ['spaces'] });
      });
    },
    [queryClient],
  );

  const filteredConversations = useMemo(() => {
    let result = conversations;

    // When a space is actively selected, show only its conversations
    if (activeSpaceId && !searchQuery.trim()) {
      result = result.filter((c) => c.spaceId === activeSpaceId);
    } else if (!searchQuery.trim()) {
      // "Recent" section: only show conversations NOT in any space
      result = result.filter((c) => !c.spaceId);
    }

    return result;
  }, [conversations, activeSpaceId, searchQuery]);

  return (
    <aside
      className="w-[260px] shrink-0 border-r border-wayne-hairline flex flex-col overflow-hidden bg-wayne-bg"
      style={{ height: 'calc(100vh - 56px)' }}
      aria-label="Conversations"
    >
      {/* Top: New chat button */}
      <div className="shrink-0" style={{ padding: '20px 16px 12px' }}>
        <button
          type="button"
          onClick={onNewChat}
          disabled={isCreating}
          className="flex items-center justify-center gap-2 w-full rounded-full bg-wayne-accent text-white border-none cursor-pointer hover:opacity-90 active:scale-[0.97] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ padding: '10px 16px', fontSize: 13, fontWeight: 500 }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {isCreating ? 'Creating...' : 'New chat'}
        </button>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 min-h-0 overflow-y-auto" style={{ padding: '0 12px 16px', scrollbarWidth: 'thin' }}>
        {/* Spaces section — always show (with create option) */}
        <div style={{ marginTop: 20 }}>
          <div
            className="flex items-center justify-between"
            style={{ padding: '0 8px 8px' }}
          >
            <span
              className="uppercase text-wayne-ink-3"
              style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em' }}
            >
              Spaces
            </span>
            <button
              type="button"
              onClick={() => {
                const name = window.prompt('Space name:');
                if (name?.trim()) {
                  void wayneFetch('/sessions/spaces', {
                    method: 'POST',
                    body: JSON.stringify({ name: name.trim() }),
                  }).then(() => {
                    void queryClient.invalidateQueries({ queryKey: ['spaces'] });
                  });
                }
              }}
              className="text-wayne-ink-3 hover:text-wayne-ink transition-colors"
              title="Create space"
              aria-label="Create new space"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>
          {spaces.length > 0 && spaces.map((space) => (
            <SpaceRow
              key={space.id}
              space={space}
              isExpanded={expandedSpaces.has(space.id)}
              isActive={activeSpaceId === space.id}
              conversations={conversations.filter((c) => c.spaceId === space.id)}
              onToggleExpand={() => toggleSpace(space.id)}
              onSelect={() =>
                onSelectSpace(activeSpaceId === space.id ? null : space.id)
              }
              onSelectConversation={onSelectConversation}
              activeConversationId={activeConversationId}
              onDropConversation={(sessionId) => handleDropOnSpace(space.id, sessionId)}
            />
          ))}
          {spaces.length === 0 && (
            <p className="text-wayne-ink-3 text-center" style={{ fontSize: 11, padding: '8px' }}>
              No spaces yet. Click + to create one.
            </p>
          )}
        </div>

        {/* Recent conversations section */}
        <div style={{ marginTop: 20 }}>
          <div
            className="uppercase text-wayne-ink-3"
            style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', padding: '0 8px 8px' }}
          >
            {activeSpaceId
              ? `${spaces.find((s) => s.id === activeSpaceId)?.name ?? 'Filtered'}`
              : 'Recent'}
          </div>

          {/* Loading state */}
          {(sessionsLoading || isSearching) && (
            <p
              className="text-wayne-ink-3"
              style={{ fontSize: 12, lineHeight: 1.5, padding: '6px 10px' }}
            >
              {isSearching ? 'Searching...' : 'Loading conversations...'}
            </p>
          )}

          {/* Empty state */}
          {!sessionsLoading && !isSearching && filteredConversations.length === 0 && (
            <p
              className="text-wayne-ink-3"
              style={{ fontSize: 12, lineHeight: 1.5, padding: '6px 10px' }}
            >
              {searchQuery.trim()
                ? 'No conversations found'
                : 'No conversations yet. Start a new chat!'}
            </p>
          )}

          {/* Conversation list */}
          {!sessionsLoading && filteredConversations.map((conv, index) => (
            <ConversationContextMenu key={conv.id} sessionId={conv.id}>
              <ConversationRow
                conversation={conv}
                isActive={conv.id === activeConversationId}
                index={index}
                onSelect={() => onSelectConversation(conv.id)}
              />
            </ConversationContextMenu>
          ))}
        </div>
      </div>

      {/* Bottom border divider (matches mockup conv-panel__bottom) */}
      <div className="shrink-0 border-t border-wayne-hairline" style={{ padding: '12px 16px' }}>
        {/* Search input placed at bottom */}
        <div className="relative">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-wayne-ink-3"
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-wayne-hairline bg-wayne-surface text-wayne-ink placeholder:text-wayne-ink-3 focus:outline-none focus:border-wayne-accent transition-colors"
          />
        </div>
      </div>
    </aside>
  );
}

function SpaceRow({
  space,
  isExpanded,
  isActive,
  conversations,
  onToggleExpand,
  onSelect,
  onSelectConversation,
  activeConversationId,
  onDropConversation,
}: {
  readonly space: SpaceItem;
  readonly isExpanded: boolean;
  readonly isActive: boolean;
  readonly conversations: readonly ConversationItem[];
  readonly onToggleExpand: () => void;
  readonly onSelect: () => void;
  readonly onSelectConversation: (id: string) => void;
  readonly activeConversationId: string;
  readonly onDropConversation: (sessionId: string) => void;
}): ReactElement {
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <>
      {/* Space item row */}
      <button
        type="button"
        onClick={onToggleExpand}
        onDoubleClick={onSelect}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          const sessionId = e.dataTransfer.getData('text/session-id');
          if (sessionId) onDropConversation(sessionId);
        }}
        className={`flex items-center w-full cursor-pointer rounded-lg transition-colors text-left ${
          isDragOver
            ? 'bg-wayne-accent/10 ring-2 ring-wayne-accent/40'
            : isActive
              ? 'bg-wayne-surface-2'
              : 'hover:bg-wayne-surface-2'
        }`}
        style={{ gap: 10, padding: '8px 10px' }}
        aria-pressed={isActive}
        aria-expanded={isExpanded}
      >
        {/* Chevron */}
        <svg
          className="shrink-0 text-wayne-ink-3 transition-transform"
          style={{
            width: 12,
            height: 12,
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transitionDuration: '250ms',
            transitionTimingFunction: 'cubic-bezier(0.2, 1, 0.3, 1)',
          }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        {/* Folder icon */}
        <svg
          className="shrink-0 text-wayne-ink-3"
          style={{ width: 15, height: 15 }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
        {/* Name */}
        <span
          className="flex-1 text-wayne-ink-2 truncate"
          style={{ fontSize: 13 }}
        >
          {space.name}
        </span>
        {/* Badge */}
        <span
          className="shrink-0 text-wayne-ink-3 bg-wayne-surface-2 rounded-full"
          style={{ fontSize: 10, fontFamily: 'var(--font-mono, monospace)', padding: '1px 7px' }}
        >
          {space.count}
        </span>
      </button>

      {/* Space children (expanded list) */}
      <div
        style={{
          paddingLeft: 20,
          marginTop: 2,
          overflow: 'hidden',
          maxHeight: isExpanded ? 500 : 0,
          opacity: isExpanded ? 1 : 0,
          transition: 'max-height 350ms cubic-bezier(0.2, 1, 0.3, 1), opacity 250ms cubic-bezier(0.2, 1, 0.3, 1)',
        }}
      >
        {conversations.map((conv) => {
          const agent = AGENTS[conv.agentId] ?? AGENTS.developer;
          return (
            <button
              key={conv.id}
              type="button"
              onClick={() => onSelectConversation(conv.id)}
              className={`flex items-center w-full text-left rounded-md cursor-pointer text-wayne-ink-2 transition-colors hover:bg-wayne-surface-2 ${
                conv.id === activeConversationId ? 'bg-wayne-surface-2' : ''
              }`}
              style={{ gap: 8, padding: '6px 10px', fontSize: 12 }}
            >
              <span
                className="shrink-0 rounded-full"
                style={{ width: 6, height: 6, backgroundColor: agent.light }}
                aria-hidden="true"
              />
              <span className="truncate">{conv.title}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

function ConversationRow({
  conversation,
  isActive,
  index,
  onSelect,
}: {
  readonly conversation: ConversationItem;
  readonly isActive: boolean;
  readonly index: number;
  readonly onSelect: () => void;
}): ReactElement {
  const stagger = useStaggerAnimation(index);
  const agent = AGENTS[conversation.agentId] ?? AGENTS.developer;

  return (
    <button
      type="button"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/session-id', conversation.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={onSelect}
      className={`flex items-center w-full cursor-pointer rounded-lg text-left transition-all ${
        isActive
          ? 'bg-wayne-surface-2 border-wayne-hairline'
          : 'border-transparent hover:bg-wayne-surface-2'
      } ${stagger.className}`}
      style={{
        gap: 10,
        padding: '9px 10px',
        borderRadius: 8,
        border: '1px solid',
        borderColor: isActive ? 'var(--wayne-hairline)' : 'transparent',
        ...stagger.style,
      }}
      aria-current={isActive ? 'true' : undefined}
    >
      <span
        className="shrink-0 rounded-full"
        style={{ width: 7, height: 7, backgroundColor: agent.light }}
        aria-hidden="true"
      />
      <span className="flex-1 min-w-0">
        <span
          className="block text-wayne-ink truncate"
          style={{ fontSize: 13, lineHeight: 1.3 }}
        >
          {conversation.title}
        </span>
      </span>
      <span
        className="text-wayne-ink-3 shrink-0"
        style={{ fontSize: 11, fontFamily: 'var(--font-mono, monospace)' }}
      >
        {conversation.time}
      </span>
    </button>
  );
}

// -- Connection status indicator ----------------------------------------------

/** Map WsStatus to a dot color and optional label. */
function getStatusIndicator(status: WsStatus): {
  dotColor: string;
  label: string | null;
} {
  switch (status) {
    case 'open':
      return { dotColor: 'bg-wayne-green', label: null };
    case 'connecting':
      return { dotColor: 'bg-wayne-orange', label: 'Connecting' };
    case 'reconnecting':
      return { dotColor: 'bg-wayne-orange', label: 'Reconnecting' };
    case 'closed':
      return { dotColor: 'bg-wayne-ink-3', label: 'Disconnected' };
    case 'idle':
    default:
      return { dotColor: 'bg-wayne-ink-3', label: null };
  }
}

function WsStatusIndicator({ status }: { readonly status: WsStatus }) {
  const { dotColor, label } = getStatusIndicator(status);

  // Don't render at all for idle — the bridge isn't expected to be active yet
  // (provider not mounted or session not started).
  if (status === 'idle') return null;

  return (
    <span
      className="inline-flex items-center gap-1.5"
      role="status"
      aria-label={`WebSocket ${status}`}
    >
      <span
        className={`inline-block w-2 h-2 rounded-full ${dotColor}`}
        aria-hidden="true"
      />
      {label && (
        <span className="text-xs text-wayne-ink-2">{label}</span>
      )}
    </span>
  );
}


export default function Play(): ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { className: pageEnterClass } = usePageEnter();

  // Search state — lifted here so we can pass to both the panel and the query
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  // Space filter state — null means "show all"
  const [activeSpaceId, setActiveSpaceId] = useState<string | null>(null);

  // Share modal state
  const [shareOpen, setShareOpen] = useState(false);

  // -- Fetch sessions list from real API ---------------------------------------
  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    queryKey: ['sessions', 'list'],
    queryFn: async () => {
      const result = await listSessionsGenerated({ query: { limit: 50 } });
      return result.data?.sessions ?? [];
    },
    staleTime: 30_000,
  });

  // -- Search sessions via real API (debounced) --------------------------------
  const { data: searchResults, isFetching: isSearching } = useQuery({
    queryKey: ['sessions', 'search', debouncedQuery],
    queryFn: async () => {
      const result = await searchSessionsGenerated({ query: { query: debouncedQuery, limit: 50 } });
      return result.data?.sessions ?? [];
    },
    enabled: debouncedQuery.length > 2,
    staleTime: 15_000,
  });

  // -- Fetch spaces from real API (routed through /sessions/spaces) -------------
  const { data: spacesData } = useQuery({
    queryKey: ['spaces'],
    queryFn: () => wayneFetch<SpacesResponse>('/sessions/spaces'),
    staleTime: 60_000,
  });

  // -- Derive conversation items from API data ---------------------------------
  const conversations: readonly ConversationItem[] = useMemo(() => {
    // If searching and we have results, use those
    const sessions: readonly SessionSummary[] =
      debouncedQuery.length > 2 && searchResults
        ? searchResults
        : sessionsData ?? [];

    return sessions.map((s) => {
      const rawSpaceId = (s as SessionSummary & { spaceId?: string }).spaceId;
      const item: ConversationItem = {
        id: s.sessionId,
        title: s.title || 'New conversation',
        agentId: (s.lastRole ?? 'developer') as AgentId,
        time: formatRelativeTime(s.updatedAt),
        ...(rawSpaceId ? { spaceId: rawSpaceId } : {}),
      };
      return item;
    });
  }, [sessionsData, searchResults, debouncedQuery]);

  // -- Derive spaces from API data ---------------------------------------------
  const spaces: readonly SpaceItem[] = useMemo(() => {
    if (!spacesData?.spaces) return [];
    return spacesData.spaces.map((s) => ({
      id: s.spaceId,
      name: s.name,
      count: conversations.filter((c) => c.spaceId === s.spaceId).length,
    }));
  }, [spacesData, conversations]);

  // -- Create session mutation -------------------------------------------------
  const createSessionMutation = useMutation({
    mutationFn: () => createSession(),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
      navigate(`/play?session=${data.sessionId}`);
    },
  });

  // Read query params: ?session= for resuming, ?seed= for auto-sending a prompt
  const sessionParam = searchParams.get('session');
  const seedParam = searchParams.get('seed');

  // If ?session= is a valid (non-empty, non-'undefined') value, use it directly;
  // otherwise fall back to the useActiveSession hook which gets/creates a session.
  const hasExplicitSession =
    sessionParam != null && sessionParam !== '' && sessionParam !== 'undefined';

  const activeSession = useActiveSession();

  // Resolved session: explicit param wins over auto-resolved.
  const sessionId = hasExplicitSession ? sessionParam : activeSession.sessionId;
  const isLoading = hasExplicitSession ? false : activeSession.isLoading;
  const error = hasExplicitSession ? null : activeSession.error;

  // Track active conversation in the panel — derives from current session ID
  const activeConvId = sessionId ?? '';

  // Once the session is resolved (via hook), update the URL so bookmarks work.
  const lastAutoSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (sessionId && !hasExplicitSession && lastAutoSessionRef.current !== sessionId) {
      lastAutoSessionRef.current = sessionId;
      navigate(`/play?session=${sessionId}`, { replace: true });
    }
  }, [sessionId, hasExplicitSession, navigate]);

  // Subscribe to WS for the active session. Even ephemeral sessions (play-*)
  // need a WS connection when the backend dispatches async (status: "streaming").
  const wsSessionId = sessionId ?? undefined;
  const { status: wsStatus } = useWsSession(wsSessionId);

  const handleNewChat = useCallback(() => {
    createSessionMutation.mutate();
  }, [createSessionMutation]);

  const handleSelectConversation = useCallback(
    (id: string) => {
      navigate(`/play?session=${id}`);
    },
    [navigate],
  );

  return (
    <AccessibleAnnouncer>
      <div className={`flex h-full overflow-hidden ${pageEnterClass}`}>
        {/* Left: Conversation Panel */}
        <ConversationPanel
          conversations={conversations}
          spaces={spaces}
          activeConversationId={activeConvId}
          onSelectConversation={handleSelectConversation}
          onNewChat={handleNewChat}
          activeSpaceId={activeSpaceId}
          onSelectSpace={setActiveSpaceId}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          isSearching={isSearching}
          sessionsLoading={sessionsLoading}
          isCreating={createSessionMutation.isPending}
        />

        {/* Center: Chat Area */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden py-12 px-16 pb-24">
          {/* Share Modal */}
          {sessionId && (
            <ShareModal
              open={shareOpen}
              onOpenChange={setShareOpen}
              sessionId={sessionId}
            />
          )}
          <div className="max-w-[920px] w-full mx-auto flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="flex items-center justify-between mb-3 shrink-0">
              <div className="flex items-center gap-3">
                <h1
                  className="font-serif text-wayne-ink text-[40px]"
                  style={{
                    fontWeight: 500,
                    letterSpacing: '-0.012em',
                    lineHeight: 1.2,
                  }}
                >
                  Play
                </h1>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-wayne-ink-2 border border-wayne-hairline hover:bg-wayne-surface-2 transition-colors"
                  aria-label="Share conversation"
                >
                  <Share2 size={14} aria-hidden="true" />
                  Share
                </button>
              </div>
              <WsStatusIndicator status={wsStatus} />
            </div>

            {isLoading && (
              <div
                className="flex items-center justify-center bg-wayne-surface border border-wayne-hairline rounded-xl flex-1"
                style={{ minHeight: 540 }}
                role="status"
                aria-label="Starting session"
              >
                <p className="text-wayne-ink-2" style={{ fontSize: 16 }}>
                  Starting session&hellip;
                </p>
              </div>
            )}

            {error && (
              <div
                className="flex items-center justify-center bg-wayne-surface border border-wayne-hairline rounded-xl flex-1"
                style={{ minHeight: 540 }}
                role="alert"
              >
                <p className="text-wayne-ink-2" style={{ fontSize: 16 }}>
                  {error}
                </p>
              </div>
            )}

            {sessionId && !isLoading && !error && (
              <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                <PlayChat key={sessionId} sessionId={sessionId} seedPrompt={seedParam} />
              </div>
            )}
          </div>
        </div>
      </div>
    </AccessibleAnnouncer>
  );
}

// ---------------------------------------------------------------------------
// PlayChat — inner component rendered only once sessionId is resolved.
// Separated so `useChat` is never called with a null/empty session ID.
// ---------------------------------------------------------------------------

function PlayChat({
  sessionId,
  seedPrompt,
}: {
  readonly sessionId: string;
  readonly seedPrompt: string | null;
}): ReactElement {
  // The WS subscription is handled at the parent Play level (for status display).
  // This component focuses on the chat UI wired to the session.

  const {
    messages,
    isStreaming,
    currentAgents,
    runShape,
    errorBanner,
    sendMessage,
    stopGeneration,
    clearError,
  } = useChat({ sessionId });

  const { sessionFiles, registerFile } = useSessionFiles(sessionId);

  // Auto-send the seed prompt (from quick-prompt cards on Home) once on mount.
  const didSendSeed = useRef(false);
  useEffect(() => {
    if (seedPrompt && !didSendSeed.current) {
      didSendSeed.current = true;
      void sendMessage(seedPrompt);
    }
  }, [seedPrompt, sendMessage]);

  const activeAgentLabels = useMemo(
    () =>
      currentAgents
        .map((a) => AGENTS[a.agentId]?.displayName ?? a.agentId)
        .join(', '),
    [currentAgents],
  );

  const subtitle = isStreaming
    ? `${activeAgentLabels || 'Agents'} · ${runShape}`
    : 'Ask Developer to explain the auth flow, or @Manager for status.';

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <p
        className="text-wayne-ink-2 mb-4 shrink-0"
        style={{ fontSize: 16, lineHeight: 1.55 }}
      >
        {subtitle}
      </p>
      <div
        className="bg-wayne-surface border border-wayne-hairline rounded-xl overflow-hidden flex flex-col flex-1 min-h-0"
        style={{ maxHeight: 'calc(100vh - 220px)' }}
      >
        <ChatPanel
          sessionId={sessionId}
          messages={messages}
          errorBanner={errorBanner}
          onClearError={clearError}
          isStreaming={isStreaming}
          composer={
            <ChatComposer
              isStreaming={isStreaming}
              onSend={(text, agent, attachments) => {
                void sendMessage(text, { ...(agent ? { agent } : {}), ...(attachments ? { attachments } : {}) });
              }}
              onStop={stopGeneration}
              sessionFiles={sessionFiles}
              onFileUploaded={registerFile}
              onFullAutoSubmit={(parsed) => {
                // /full-auto and /auto bypass the regular chat send and post
                // directly to /api/chat with {source: 'slash',
                // confirmedFullAuto: true} so the chat_processor skips the
                // natural-language confirmation card. The ack/live cards
                // stream back over the same WebSocket session.
                void submitChatFullAuto({ sessionId, text: parsed.raw });
              }}
            />
          }
        />
      </div>
    </div>
  );
}
